import { SegmentedControl, Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Info } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { GenerationType, Medium } from "../../../api/generated/model";
import { accessScopeQuery } from "../../../api/session";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { GenerationDetailModal } from "../../../platform/jobs/detail/GenerationDetailModal";
import { GENERATIONS_PAGE, useJobList } from "../../../platform/jobs/job-list";
import { modelTitle } from "../../../platform/jobs/model-name";
import { useStreamStatus } from "../../../platform/stream-status";
import home from "../pro-home.module.css";
import { ResultCard } from "../ResultCard";
import classes from "./create.module.css";

type Who = "mine" | "team";

/**
 * The prototype's "Recent … results" beside a Create page's composer: the Studio's own My
 * Generations read of this medium (5 a page, its key), the user's or the team's, newest first.
 */
interface RecentResultsProps {
  teamId: string;
  brandId: string;
  /** One medium's jobs, as the Studio's Image and Video pages read them, or these types alone. */
  medium?: Medium | undefined;
  types?: readonly GenerationType[] | undefined;
  /** The mode's word in "Recent … results". */
  noun: string;
  /** A link beside "All generations": the Voice and sound page's to the sounds library. */
  more?: ReactNode;
}

export function RecentResults(props: RecentResultsProps) {
  const { teamId, brandId, medium, types, noun, more } = props;
  const [who, setWho] = useState<Who>("mine");
  // The job whose detail is open over the page, or none.
  const [open, setOpen] = useState<string | null>(null);
  const userId = useQuery({ ...accessScopeQuery(), select: (scope) => scope.user_id }).data;
  const jobs = useJobList(
    teamId,
    { status: "all", brandId, medium, types, pageSize: GENERATIONS_PAGE },
    useStreamStatus(),
  );
  const catalog = useGetCatalog().data;
  const all = jobs.data?.pages.flatMap((page) => page.items);
  const shown = all?.filter((job) => who === "team" || job.created_by === userId);
  const readAgain = () => void jobs.refetch();
  return (
    <>
      <div className={classes.resultsHead}>
        <h2 className={classes.resultsTitle}>Recent {noun} results</h2>
        <SegmentedControl
          aria-label="Whose results"
          radius="xl"
          size="xs"
          classNames={{ root: classes.seg, label: classes.segLabel }}
          value={who}
          onChange={(value) => setWho(value === "team" ? "team" : "mine")}
          data={[
            { value: "mine", label: "Mine" },
            { value: "team", label: "Team" },
          ]}
        />
        <span className={classes.links}>
          {more}
          <Link
            to="/teams/$teamId/brands/$brandId/pro/generations"
            params={{ teamId, brandId }}
            className={home.subtle}
          >
            All generations <ArrowRight size={14} aria-hidden />
          </Link>
        </span>
      </div>
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {shown === undefined ? (
        <div className={classes.gallery}>
          {[0, 1, 2].map((each) => (
            <Skeleton key={each} className={home.mediaSkeleton} />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className={classes.empty}>
          {/* Mine sees only the pages read so far: with more, its results may be further on. */}
          {jobs.hasNextPage ? (
            <b>None of yours in the latest results</b>
          ) : (
            <b>Nothing here yet</b>
          )}
          <span className={home.small}>
            Your results show up here as they finish and are kept in Generations automatically.
          </span>
        </div>
      ) : (
        <div className={classes.gallery}>
          {shown.map((job) => (
            <ResultCard
              key={job.id}
              job={job}
              model={modelTitle(catalog, job)}
              onLost={readAgain}
              onOpen={() => setOpen(job.id)}
            />
          ))}
        </div>
      )}
      {jobs.hasNextPage ? (
        <button
          type="button"
          className={home.smallButton}
          disabled={jobs.isFetchingNextPage}
          onClick={() => void jobs.fetchNextPage()}
        >
          Show more
        </button>
      ) : null}
      <p className={classes.alert}>
        <Info size={16} aria-hidden />
        <span>
          Every result saves itself to <b>Generations</b>. Click one for its prompt, settings and
          next steps.
        </span>
      </p>
      {open === null ? null : (
        <GenerationDetailModal teamId={teamId} jobId={open} onClose={() => setOpen(null)} />
      )}
    </>
  );
}
